import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Phone, SunMedium, Lightbulb, ShieldCheck, Layers, Car, Cctv, Check } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/lib/motion";
import { AddToEnquiryButton, slugify } from "@/components/site/AddToEnquiryButton";
import {
  CONTACT, WINDOW_TINTS, HEADLIGHT_TINTS, PPF_PANELS, PPF_PACKAGES, FULL_PPF, DASH_CAMS, DASH_POINTS,
} from "@/data/site";

const PriceList = ({ rows, testid, group, prefix }) => (
  <div data-testid={testid} className="card-line overflow-hidden rounded-2xl">
    {rows.map(([label, price], i) => (
      <div key={label}
        className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-4 transition-colors duration-200 hover:bg-white/[0.03] sm:px-6 ${i !== 0 ? "border-t border-[var(--line)]" : ""}`}>
        <span className="text-sm text-[var(--white)]/90">{label}</span>
        <div className="flex items-center gap-4">
          <span className="font-display text-base font-800 text-[var(--blue-bright)] sm:text-lg">{price}</span>
          <AddToEnquiryButton id={`${prefix}-${slugify(label)}`} name={`${group} — ${label}`} price={price} compact />
        </div>
      </div>
    ))}
  </div>
);

const Block = ({ id, icon: Icon, eyebrow, title, sub, children }) => (
  <section id={id} className="scroll-mt-24">
    <Reveal className="mb-7">
      <span className="eyebrow mb-3"><Icon size={15} /> {eyebrow}</span>
      <h2 className="font-display text-3xl font-800 leading-tight tracking-tight text-white sm:text-4xl">{title}</h2>
      {sub && <p className="mt-3 max-w-2xl text-sm text-[var(--muted)]">{sub}</p>}
    </Reveal>
    <Reveal delay={0.05}>{children}</Reveal>
  </section>
);

export default function Services() {
  return (
    <div className="relative bg-[#08090B]">
      <Navbar />

      <header className="relative overflow-hidden pt-32 pb-16">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_0%,rgba(23,147,255,0.22),transparent_50%)]" />
        <div className="absolute inset-0 hero-grid-lines opacity-30" />
        <div className="container-x relative">
          <Link to="/" data-testid="back-home" className="mb-6 inline-flex items-center gap-2 text-sm text-[var(--muted)] transition-colors hover:text-white">
            <ArrowLeft size={16} /> Back to home
          </Link>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl font-display text-4xl font-900 leading-[1.02] tracking-tight text-white sm:text-6xl">
            Services &amp; <span className="text-[var(--blue-bright)]">Pricing</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7 }}
            className="mt-5 max-w-2xl text-base text-[var(--muted)]">
            Transparent, up-front pricing across tints, PPF and dash cams. Detailing, valets, polishing and
            cleaning quoted on request — just give us a call.
          </motion.p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href={CONTACT.phoneHref} data-testid="services-call" className="btn-electric"><Phone size={15} /> {CONTACT.phone}</a>
            <Link to="/#contact" className="btn-ghost">Request a quote</Link>
          </div>
        </div>
      </header>

      <main className="container-x relative z-10 space-y-20 pb-28">
        <Block id="window-tints" icon={SunMedium} eyebrow="Window Tints" title="Window Tint Prices"
          sub="Precision-cut tints for style, privacy and heat reduction — fitted bubble-free.">
          <PriceList rows={WINDOW_TINTS} testid="table-window-tints" group="Window Tint" prefix="wt" />
        </Block>

        <Block id="headlight-tints" icon={Lightbulb} eyebrow="Headlight Tints" title="Headlight Tint Prices"
          sub="From subtle smoke to premium gloss and coloured finishes.">
          <PriceList rows={HEADLIGHT_TINTS} testid="table-headlight-tints" group="Headlight Tint" prefix="ht" />
        </Block>

        <Block id="ppf-panels" icon={ShieldCheck} eyebrow="PPF · Individual Panels" title="PPF Individual Panels"
          sub="Self-healing paint protection film applied to individual panels.">
          <PriceList rows={PPF_PANELS} testid="table-ppf-panels" group="PPF Panel" prefix="ppfp" />
        </Block>

        <Block id="ppf-packages" icon={Layers} eyebrow="PPF · Packages" title="PPF Packages"
          sub="Bundled coverage for the best value and protection.">
          <div data-testid="ppf-packages" className="grid gap-5 md:grid-cols-3">
            {PPF_PACKAGES.map((p, i) => (
              <div key={p.title}
                className={`relative overflow-hidden rounded-2xl border p-7 transition-all duration-300 hover:-translate-y-1 ${
                  i === 1 ? "border-[var(--blue)]/50 bg-gradient-to-b from-[#13181F] to-[#0B0E12] glow-ring" : "card-line hover:border-[var(--blue)]/40"
                }`}>
                {i === 1 && <span className="absolute right-4 top-4 rounded-full bg-[var(--blue)]/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--blue-bright)]">Popular</span>}
                <h3 className="font-display text-lg font-700 text-white">{p.title}</h3>
                <p className="mt-3 min-h-[48px] text-sm leading-relaxed text-[var(--muted)]">{p.desc}</p>
                <p className="mt-5 font-display text-3xl font-900 text-[var(--blue-bright)]">{p.price}</p>
                <div className="mt-5">
                  <AddToEnquiryButton id={`ppfpkg-${slugify(p.title)}`} name={`PPF ${p.title}`} price={p.price} />
                </div>
              </div>
            ))}
          </div>
        </Block>

        <Block id="full-ppf" icon={Car} eyebrow="PPF · Full Vehicle" title="Full Vehicle PPF"
          sub="Complete coverage to keep the whole vehicle flawless.">
          <PriceList rows={FULL_PPF} testid="table-full-ppf" group="Full Vehicle PPF" prefix="fppf" />
        </Block>

        <Block id="dash-cams" icon={Cctv} eyebrow="Dash Cam Supply & Fit" title="Dash Cam Supply & Fit"
          sub="Professional hardwired installs with hidden cabling and parking mode.">
          <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
            <PriceList rows={DASH_CAMS} testid="table-dash-cams" group="Dash Cam" prefix="dc" />
            <div className="card-line rounded-2xl p-7">
              <h3 className="font-display text-lg font-700 text-white">Why our installs</h3>
              <ul className="mt-5 space-y-3">
                {DASH_POINTS.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm text-[var(--white)]/85">
                    <Check size={16} className="mt-0.5 shrink-0 text-[var(--blue-bright)]" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Block>

        <Reveal>
          <div className="overflow-hidden rounded-[26px] border border-[var(--blue)]/30 bg-gradient-to-r from-[#12161C] to-[#0B0E12] p-9 text-center sm:p-12">
            <h2 className="font-display text-3xl font-800 text-white sm:text-4xl">Not sure which package you need?</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-[var(--muted)]">
              Tell us your vehicle and what you're after — we'll recommend the right option and give you a clear price.
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <a href={CONTACT.phoneHref} className="btn-electric"><Phone size={15} /> Call {CONTACT.phone}</a>
              <Link to="/#contact" className="btn-ghost">Send an enquiry</Link>
            </div>
          </div>
        </Reveal>
      </main>

      <Footer />
    </div>
  );
}
